// OCT-7340: TreatyApp: User Management — add an Action History view of admin activity - Frontend : ashish.atwal@resolutionlife.com
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  ActionHistoryEntryFragment,
  useActionHistoryFilterOptionsQuery,
  useActionHistoryLazyQuery,
} from '../../graphql/generated';

const PAGE_LIMIT = 50;

export type ActionHistoryServerFilter = {
  actionType?: string;
  userId?: string;
  from?: Date | null;
  to?: Date | null;
};

type UseActionHistoryBag = {
  loading: boolean;
  error: boolean;
  retry: () => void;
  entries: ActionHistoryEntryFragment[];
  count: number;
  hasMore: boolean;
  isFetchingMore: boolean;
  fetchMore: () => void;
};

const toIso = (date?: Date | null): string | undefined =>
  date ? date.toISOString() : undefined;

export const useActionHistory = (
  isOpen: boolean,
  filter: ActionHistoryServerFilter = {}
): UseActionHistoryBag => {
  const [entries, setEntries] = useState<ActionHistoryEntryFragment[]>([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const offsetRef = useRef(0);

  const [executeQuery] = useActionHistoryLazyQuery({
    fetchPolicy: 'no-cache',
  });

  const buildVariables = useCallback(
    (offset: number) => ({
      filter: {
        actionType: filter.actionType || undefined,
        userId: filter.userId || undefined,
        from: toIso(filter.from),
        to: toIso(filter.to),
        pagination: { limit: PAGE_LIMIT, offset },
      },
    }),
    [filter.actionType, filter.userId, filter.from, filter.to]
  );

  // Initial load + reload when filters change.
  useEffect(() => {
    if (!isOpen) return;

    setLoading(true);
    setError(false);
    setEntries([]);
    offsetRef.current = 0;

    executeQuery({ variables: buildVariables(0) })
      .then(({ data, error: err }) => {
        if (err) {
          setError(true);
        } else {
          const page = data?.actionHistory?.entries ?? [];
          setEntries(page);
          setCount(data?.actionHistory?.count ?? 0);
          offsetRef.current = page.length;
        }
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [isOpen, buildVariables, executeQuery]);

  const hasMore = entries.length < count;

  const fetchMore = useCallback(() => {
    if (isFetchingMore || !hasMore) return;
    setIsFetchingMore(true);

    executeQuery({ variables: buildVariables(offsetRef.current) })
      .then(({ data, error: err }) => {
        if (err) {
          setError(true);
        } else {
          const page = data?.actionHistory?.entries ?? [];
          setEntries((prev) => [...prev, ...page]);
          setCount(data?.actionHistory?.count ?? 0);
          offsetRef.current += page.length;
        }
      })
      .catch(() => setError(true))
      .finally(() => setIsFetchingMore(false));
  }, [isFetchingMore, hasMore, buildVariables, executeQuery]);

  const retry = useCallback(() => {
    setLoading(true);
    setError(false);
    setEntries([]);
    offsetRef.current = 0;

    executeQuery({ variables: buildVariables(0) })
      .then(({ data, error: err }) => {
        if (err) {
          setError(true);
        } else {
          const page = data?.actionHistory?.entries ?? [];
          setEntries(page);
          setCount(data?.actionHistory?.count ?? 0);
          offsetRef.current = page.length;
        }
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [buildVariables, executeQuery]);

  return {
    loading,
    error,
    retry,
    entries,
    count,
    hasMore,
    isFetchingMore,
    fetchMore,
  };
};

type ActionHistoryUserOption = { id: string; name: string };

type UseActionHistoryOptionsBag = {
  loading: boolean;
  users: ActionHistoryUserOption[];
  actionTypes: string[];
};

export const useActionHistoryOptions = (
  isOpen: boolean
): UseActionHistoryOptionsBag => {
  const { data, loading } = useActionHistoryFilterOptionsQuery({
    fetchPolicy: 'no-cache',
    skip: !isOpen,
  });

  return {
    loading,
    users: data?.actionHistoryFilterOptions?.users ?? [],
    actionTypes: data?.actionHistoryFilterOptions?.actionTypes ?? [],
  };
};
